/**
 * The settings screen (TRD-09 §12).
 *
 * Every change persists to the save IMMEDIATELY (R-09-28) — a settings screen with an
 * Apply button is a screen that loses changes. The VFX-facing subset also flows
 * straight into the live Vfx settings so a shake slider is felt on the next wave, not
 * after a reload. Volumes persist now and start mattering the moment the audio backend
 * lands (the AudioBus already honours them).
 */
import type { Settings } from '../persistence/save.js';

interface SettingsScreenProps {
  settings: Settings;
  displayName: string;
  onChange: (patch: Partial<Settings>) => void;
  onDisplayName: (name: string) => void;
  onBack: () => void;
}

function Slider({
  label,
  value,
  onChange,
  max = 1,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  max?: number;
}) {
  return (
    <label className="settings__row">
      <span>{label}</span>
      <input
        type="range"
        min={0}
        max={max}
        step={0.05}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className="muted settings__value">{Math.round(value * 100)}%</span>
    </label>
  );
}

function Toggle({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <label className="settings__row" title={hint}>
      <span>{label}</span>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

export function SettingsScreen({
  settings,
  displayName,
  onChange,
  onDisplayName,
  onBack,
}: SettingsScreenProps) {
  return (
    <div className="menu">
      <h1 className="menu__title menu__title--small">SETTINGS</h1>

      <div className="settings">
        <span className="label">Identity</span>
        <label className="settings__row">
          <span>Display name</span>
          <input
            type="text"
            maxLength={24}
            placeholder="anonymous"
            value={displayName}
            onChange={(e) => onDisplayName(e.target.value)}
          />
        </label>
        <p className="muted settings__hint">Shown on the daily leaderboard. Nothing else.</p>

        <span className="label">Audio</span>
        <Slider
          label="Master volume"
          value={settings.masterVolume}
          onChange={(v) => onChange({ masterVolume: v })}
        />
        <Slider
          label="Effects"
          value={settings.sfxVolume}
          onChange={(v) => onChange({ sfxVolume: v })}
        />
        <Slider
          label="Music"
          value={settings.musicVolume}
          onChange={(v) => onChange({ musicVolume: v })}
        />

        <span className="label">Feel</span>
        <Slider
          label="Screen shake"
          value={settings.screenshakeScale}
          onChange={(v) => onChange({ screenshakeScale: v })}
        />
        <label className="settings__row">
          <span>Damage numbers</span>
          <select
            value={settings.damageNumbers}
            onChange={(e) =>
              onChange({ damageNumbers: e.target.value as Settings['damageNumbers'] })
            }
          >
            <option value="all">all</option>
            <option value="crits-only">crits only</option>
            <option value="off">off</option>
          </select>
        </label>
        <Toggle
          label="Damage flash"
          value={settings.showDamageFlash}
          onChange={(v) => onChange({ showDamageFlash: v })}
        />
        <Toggle
          label="Hitstop"
          value={settings.hitstopEnabled}
          onChange={(v) => onChange({ hitstopEnabled: v })}
          hint="The brief freeze on crits and boss kills"
        />

        <Toggle
          label="Skip dead time"
          value={settings.autoFastForward}
          onChange={(v) => onChange({ autoFastForward: v })}
          hint="Speeds up while nothing is in range of anything, and drops back the moment it is"
        />

        <span className="label">Accessibility</span>
        <Toggle
          label="Colorblind mode"
          value={settings.colorblindMode}
          onChange={(v) => onChange({ colorblindMode: v })}
          hint="Adds a shape badge to every class chip, so identity never rides on colour alone"
        />
      </div>

      <button type="button" onClick={onBack}>
        ← Back
      </button>
    </div>
  );
}
